import { useEffect, useState, useContext } from "react";
import {
  useNavigate,
  Link,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Badge,
  Button,
  Col,
  Dropdown,
  Input,
  Modal,
  Row,
  Tooltip,
  message,
} from "antd";
import dayjs from "dayjs";
import { MoreHorizontal, Mail, Send, Trash2, History } from "lucide-react";
import CopyText from "../web2lead/CopyText";

import { errEnum } from "../../config";
import { ReactComponent as WhatsAppIcon } from "../../assets/svg/sidebar/whatsapp.svg";
import TrialModal from "../../common/TrialModal";
import {
  selectedRow,
  saveSearchAction,
  emptyErrorObj,
  getUnqualifiedReasons,
} from "../../actionCreator/leadListingActionCreater";
import { getUserInfo } from "../../utils/utils";
import LeadTableView from "./LeadTableView";
import LeadKanbanView from "./LeadKanbanView";
import CalenderModal from "./CalenderModal";
import { useExport } from "./hooks/useExport";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import EmailForm from "../../common/EmailForm";
import UpdateLead from "./UpdateLead";
import CreateNewLead from "./CreateNewLead";
import DeleteLead from "./DeleteLead";
import TagLeads from "./TagLeads";
import LeadLeft from "./LeadLeft";
import LeadHeaderFilter from "./LeadHeaderFilter";
import BulkUpdate from "./BulkUpdate";
import ImportLeads from "./ImportLeads";
import { renderLeadStatus } from "../../utils/renderLeadStatus";
import LeadActivityDrawer from "./LeadActivityDrawer";
import {
  getAllUsers,
  getCurrentUserPermissions,
} from "../../actionCreator/userManagementActionCreator";

const LeadContent = () => {
  const { onExport } = useExport();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [showCal, setShowCal] = useState(false);
  const [isDelete, setDelete] = useState();
  const [isEdit, setEdit] = useState(false);
  const [isImport, setImport] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const errObj = useSelector((state) => state.leadListingReducer.errObj);
  const unreadNotifications = useSelector(
    (state) => state.LeadDetailsReducer.unreadNotifications,
  );

  const showStatusName = (status) => {
    let st = "";
    for (let i = 0; i < leadStatusListing.length; i++) {
      if (status == leadStatusListing[i].id) {
        st = leadStatusListing[i].text;
        break;
      }
    }
    return st;
  };

  const getCompanyDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/company-summary/${id}/${cname}`);
  };

  const columns = [
    {
      title: "Lead Name",
      dataIndex: "name",
      fixed: "left",
      width: "20%",
      render: (record, row, index) => {
        const name = record.firstname + " " + record.lastname;

        return (
          <span className="visible-on-hover tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between tw-gap-2">
            <span
              className="tw-flex tw-items-center tw-justify-center !tw-rounded-full tw-border tw-size-[40px] !tw-min-w-[40px] !tw-h-[40px] tw-border-[#CDD2DF] tw-border-solid tw-uppercase tw-font-xl tw-font-semibold tw-cursor-pointer"
              onClick={() => getDetails(row.key)}
            >
              {record?.firstname?.[0]}
              {record?.lastname?.[0]}
            </span>
            <Tooltip title={name}>
              <span
                className="tw-truncate tw-w-full tw-cursor-pointer"
                onClick={() => getDetails(row.key)}
              >
                {name}
              </span>
            </Tooltip>

            {!!record?.allowedActions?.includes("EDIT") && (
              <UpdateLead
                title="Lead Name"
                fieldName="fullname"
                defaultValue={{
                  fullname: record?.fullname
                    ? record.fullname
                    : record.firstname + " " + record.lastname,
                  firstname: record.firstname,
                  lastname: record.lastname,
                }}
                id={row?.key}
                callback={() => onPageChange(1, paginationValue.end)}
              />
            )}
          </span>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      width: "20%",
      render: (record, row) => {
        if (!record) return <></>;
        const leadName =
          `${row.name?.firstname || ""} ${row.name?.lastname || ""}`.trim() ||
          row.name?.fullname ||
          "Lead";
        return (
          <span className="visible-on-hover tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2 tw-truncate tw-w-full">
              <Tooltip title={record}>
                <span className="tw-truncate">{record}</span>
              </Tooltip>
            </div>

            {!!row?.executiveData?.allowedActions?.includes("EDIT") && (
              <UpdateLead
                title="Mobile Number"
                fieldName="mobile"
                defaultValue={record}
                id={row?.key}
                callback={() => onPageChange(1, paginationValue.end)}
              />
            )}
          </span>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "companyName",
      width: "15%",
      render: (record, row) => {
        return (
          <span className="visible-on-hover tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between">
            <Tooltip title={row?.companyName}>
              <span
                className="tw-truncate tw-w-full tw-cursor-pointer"
                onClick={() =>
                  row?.companyId
                    ? getCompanyDetails(row?.companyId, row?.companyName)
                    : getDetails(row?.key)
                }
              >
                {row?.companyName}
              </span>
            </Tooltip>

            {!!row?.executiveData?.allowedActions?.includes("EDIT") && (
              <UpdateLead
                title="Company Name"
                fieldName="companyName"
                defaultValue={record}
                id={row?.key}
                callback={() => onPageChange(1, paginationValue.end)}
              />
            )}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "15%",
      render: (record, row) => {
        const status = showStatusName(row?.status);

        return (
          <span className="visible-on-hover tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between">
            {row?.status ? renderLeadStatus(status, "tw-truncate") : ""}

            {!!row?.executiveData?.allowedActions?.includes("EDIT") && (
              <UpdateLead
                title="Status"
                fieldName="status"
                defaultValue={{
                  status: record,
                  lostReason: row?.executiveData?.lostReason,
                  comments: row?.executiveData?.comments,
                  unqualifiedReasonsOthers:
                    row?.executiveData?.unqualifiedReasonsOthers,
                  unqualifiedReason:
                    row?.executiveData?.unqualifiedReason?.reason,
                }}
                id={row?.key}
                callback={(val) => {
                  onPageChange(1, paginationValue.end);
                  const test = companySelectedFilterList?.selectedLeadStatus
                    ?.length
                    ? !companySelectedFilterList?.selectedLeadStatus
                      ?.map((d) => d?.id)
                      .includes(val?.status) ||
                    !companySelectedFilterList?.selectedLeadStatus
                      ?.map((d) => d?.id)
                      .includes(row?.status)
                    : [17, 18, 19].includes(val?.status) ||
                    [17, 18, 19].includes(row?.status);
                  if (test)
                    dispatch(
                      selectedRow(
                        selectedRecords?.filter((d) => d !== row?.key),
                      ),
                    );
                }}
              />
            )}
          </span>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      className: "email",
      width: "20%",
      render: (record, row) => {
        const notiClientIds = unreadNotifications?.map((d) => d?.clientLeadId);
        const isUnread = notiClientIds.includes(row?.key);
        if (!record) {
          return <></>;
        }
        return (
          <div className="tw-px-2 tw-flex tw-items-center tw-justify-between tw-gap-3">
            <Tooltip title={record}>
              <span className="tw-truncate tw-w-full">{record}</span>
            </Tooltip>
            {isEmailSetupView && !row?.executiveData?.isDummy && (
              <div className="tw-bg-white tw-border tw-border-solid tw-border-border-secondary tw-rounded-lg tw-flex tw-items-center tw-px-1.5 tw-py-1 tw-gap-1">
                <div
                  className="tw-size-[24px] tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                  onClick={() =>
                    navigate(`/lead-details/${row?.key}?type=email`)
                  }
                >
                  <Badge dot={isUnread} offset={[-1, 5]}>
                    <Tooltip title="Email History">
                      <Mail size={14} className="tw-text-slate-500 tw-cursor-pointer" />
                    </Tooltip>
                  </Badge>
                </div>

                {isEmailSetupView && !row?.executiveData?.isDummy && (
                  <>
                    <span className="tw-w-[1px] tw-h-3 tw-bg-slate-200 tw-shrink-0 tw-self-center" />
                    <EmailForm
                      recipientEmail={record}
                      empId={row?.name?.employeeId}
                      payload={row?.executiveData}
                    >
                      <div className="tw-size-[24px] tw-cursor-pointer tw-flex tw-items-center tw-justify-center">
                        <Tooltip title="Send Email">
                          <Send size={14} className="tw-text-slate-500 tw-cursor-pointer" />
                        </Tooltip>
                      </div>
                    </EmailForm>
                    <span className="tw-w-[1px] tw-h-3 tw-bg-slate-200 tw-shrink-0 tw-self-center" />
                    <Tooltip title="Chat on WhatsApp">
                      <div
                        className="tw-size-[24px] tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                        onClick={() => {
                          const leadPhone = row.mobile || row.phone || "";
                          if (!leadPhone) {
                            message.warning(
                              "This lead does not have a phone number.",
                            );
                            return;
                          }
                          const leadName =
                            `${row.name?.firstname || ""} ${row.name?.lastname || ""}`.trim() ||
                            row.name?.fullname ||
                            "Lead";
                          navigate(
                            `/whatsapp?leadId=${row.key}&phone=${encodeURIComponent(leadPhone)}&name=${encodeURIComponent(leadName)}`,
                          );
                        }}
                      >
                        <WhatsAppIcon className="tw-size-[14px] hover:tw-scale-110 tw-transition-transform" />
                      </div>
                    </Tooltip>
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },

    {
      title: "",
      dataIndex: "active",
      width: "5%",
      render: (record, row) => {
        const items = [
          {
            key: "1",
            isVisible: !!row?.executiveData?.allowedActions?.includes("EDIT"),
            label: (
              <div
                className="tw-text-xs tw-font-bold tw-flex tw-items-center tw-gap-2 tw-text-slate-700 tw-py-1 tw-px-1.5"
                onClick={() => setDelete(row)}
              >
                <Trash2 size={14} className="tw-text-red-500" />
                Delete
              </div>
            ),
          },
          {
            key: "2",
            isVisible: true,
            label: (
              <div
                className="tw-text-xs tw-font-bold tw-flex tw-items-center tw-gap-2 tw-text-slate-700 tw-py-1 tw-px-1.5"
                onClick={() => setShowHistory(row)}
              >
                <History size={14} className="tw-text-slate-500" />
                History
              </div>
            ),
          },
        ].filter(Boolean);
        return (
          <>
            {![16, 17, 19].includes(row?.status) && (
              <Dropdown
                menu={{ items: items?.filter((d) => d?.isVisible) }}
                trigger={["click"]}
              >
                <Button type="text" icon={<MoreHorizontal size={18} />} />
              </Dropdown>
            )}
          </>
        );
      },
    },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    isAuthenticated: isLoggedIn,
    isEmailSetupView,
    isLeadCreate,
    user,
  } = useContext(AuthContext);
  const [executiveEmployeeList, setExecutiveEmployeeList] = useState();

  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [searchValues, setSearchValues] = useState({
    tagname: "",
    description: "",
    tagError: "",
  });
  const companyFilterList = useSelector((state) => state.leadListingReducer);
  const companySelectedFilterList = useSelector(
    (state) => state.leadListingReducer.selectedFilters,
  );
  const loading = useSelector((state) => state.CommonReducer.loading);
  const paginationValue = useSelector(
    (state) => state.leadListingReducer.paginationValue,
  );
  const selectedRecords = useSelector(
    (state) => state.leadListingReducer.selectedRecords,
  );

  useEffect(() => {
    dispatch(getUnqualifiedReasons());
    dispatch(selectedRow([]));
    dispatch(getAllUsers({}));
    dispatch(getCurrentUserPermissions(user?.id));
  }, [dispatch, user]);

  const renderSocialLinks = (socialLinks) => {
    return socialLinks?.map((link, index) => {
      if (link?.name === "facebook") {
        return (
          <Link key={index} to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20 facebook lab la-facebook"></i>
          </Link>
        );
      } else if (link?.name === "Linkedin") {
        return (
          <Link to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20 linkedin  lab la-linkedin"></i>
          </Link>
        );
      } else if (link?.name === "twitter") {
        return (
          <Link to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20  twitter la la-twitter-square"></i>
          </Link>
        );
      }
    });
  };

  useEffect(() => {
    let data = [];
    companyFilterList?.executiveEmployeeList?.forEach((record) => {
      data = [
        ...data,
        {
          key: record.id,
          executiveData: record,
          name: record,
          companyName: record?.companyName,
          companyId: record?.companyId,
          location: `${record?.city ? record?.city + "," : ""} ${record?.state ? record?.state : ""
            }`,
          email: record?.emailId,
          phone: record?.phoneNo,
          mobile: record?.mobile || record?.phoneNo,
          status: record?.status,
          social: renderSocialLinks(record?.socialLinks),
          notes: "",
          lastModifiedDate: record?.lastModifiedDate
            ? dayjs(record?.lastModifiedDate).format("DD-MM-YYYY")
            : "",
        },
      ];
    });
    setExecutiveEmployeeList(data);
  }, [companyFilterList]);

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => d?.key !== record.key,
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, record]));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = changeRows;
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => !keys?.map((d) => d?.key).includes(d?.key),
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, ...keys]));
      }
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User", // Column configuration not to be checked
      name: record.name,
    }),
    selectedRowKeys: selectedRecords?.map((i) => i?.key),
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const onPageChange = (page, pageSize) => {
    searchParams.set("page", page);
    searchParams.set("size", pageSize);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const getDetails = (id) => {
    navigate(`/lead-details/${id}`);
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
  };

  const onConfrim = () => {
    if (!searchValues.tagname) {
      setSearchValues({
        ...searchValues,
        tagError: "error",
      });
    } else {
      const { id, email, login } = getUserInfo();
      const payload = {
        accountId: login,
        dataDump: JSON.stringify(companySelectedFilterList),
        fullName: searchValues.tagname,
        emailId: email,
        source: "Executive",
        userId: id,
      };
      dispatch(saveSearchAction(payload));
      setShowModal(false);
    }
  };

  const onInputChange = (e) => {
    setSearchValues({
      ...searchValues,
      [e.target.name]: e.target.value,
    });
  };

  const redirectToSignup = () => {
    setShowModal(false);
    setOpenModal(false);
    navigate("/signup");
  };

  const tagPage = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedRecords.length) {
        setShowTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const printPage = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      window.print();
    }
  };

  const closeTagModal = () => {
    setShowTagModal(false);
  };

  const calendarShow = () => {
    navigate(`/leads-timeline`);
  };

  const closeCalenderModal = () => {
    setShowCal(false);
  };
  const handleAddLeadsForm = () => {
    setShowAddLeadModal(true);
  };

  const activeView = parseInt(searchParams.get("layout"));

  const handleMenuClick = () => {
    if (isLeadCreate) {
      handleAddLeadsForm();
    } else {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to create lead.",
      );
    }
  };

  return (
    <>
      <LeadActivityDrawer lead={showHistory} onClose={() => setShowHistory()} />
      <div>
        <LeadHeaderFilter
          tagPage={tagPage}
          downloadExcel={() => {
            checkLoginStatus();
            onExport(
              selectedRecords?.map((i) => i?.key),
              "exl",
            );
          }}
          downloadPDF={() => {
            checkLoginStatus();
            onExport(
              selectedRecords?.map((i) => i?.key),
              "pdf",
            );
          }}
          printPage={printPage}
          setDelete={setDelete}
          setEdit={setEdit}
          companyFilterList={companyFilterList}
          selectedRecords={selectedRecords}
          handleMenuClick={handleMenuClick}
        />
        <div>
          <Row id="wrapper" className="tw-mt-1.5">
            <Col md={6} xl={5} xxl={4}>
              <LeadLeft />
            </Col>
            <Col md={18} xl={19} xxl={20} className="tw-p-0">
              {activeView === 2 ? (
                <LeadKanbanView
                  calendarShow={calendarShow}
                  loading={loading}
                  rowSelection={rowSelection}
                  getDetails={getDetails}
                  executiveEmployeeList={executiveEmployeeList}
                  paginationValue={paginationValue}
                  companyFilterList={companyFilterList}
                  onPageChange={onPageChange}
                />
              ) : (
                <LeadTableView
                  loading={loading}
                  rowSelection={rowSelection}
                  columns={columns}
                  executiveEmployeeList={executiveEmployeeList}
                  paginationValue={paginationValue}
                  companyFilterList={companyFilterList}
                  onPageChange={onPageChange}
                />
              )}
            </Col>
          </Row>
        </div>
      </div>

      {errObj[errEnum.LEAD_EXECUTIVE_DOWNLOAD_ERROR] && (
        <TrialModal
          openModal={!!errObj[errEnum.LEAD_EXECUTIVE_DOWNLOAD_ERROR]}
          closeModal={() => dispatch(emptyErrorObj())}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.LEAD_EXECUTIVE_DOWNLOAD_ERROR]
                ? errObj[errEnum.LEAD_EXECUTIVE_DOWNLOAD_ERROR].message
                : "Unable to complete the action. Please try again later."}
            </div>
          }
          modalWidth="400px"
        />
      )}

      {showAddLeadModal && (
        <CreateNewLead
          openModal={true}
          setShowAddLeadModal={setShowAddLeadModal}
        />
      )}

      <TagLeads
        selectedRecords={selectedRecords?.map((d) => d?.key)}
        showTagModal={showTagModal && isLoggedIn}
        closeTagModal={closeTagModal}
      />

      {showModal ? (
        isLoggedIn ? (
          <Modal
            title={null}
            width={"400px"}
            className="delete-popup"
            closable={true}
            footer={null}
            open={showModal}
            onCancel={closeModal}
            onOk={onConfrim}
            centered
          >
            <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
              Save Search
            </div>
            <div className="pop-up errorformcontainer tw-p-6">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                  Search Name
                </span>
                <div>
                  <Input
                    name="tagname"
                    status={searchValues?.tagError}
                    value={searchValues.tagname}
                    placeholder="Name"
                    onChange={onInputChange}
                    className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200"
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
              <Button
                className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
                onClick={() => {
                  closeModal();
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
                onClick={onConfrim}
              >
                Ok
              </Button>
            </div>
          </Modal>
        ) : (
          <TrialModal
            openModal={showModal}
            closeModal={closeModal}
            redirectToSignup={redirectToSignup}
            redirect={true}
          />
        )
      ) : (
        ""
      )}

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}

      {showCal && (
        <Modal
          title="Calendar"
          width={"800px"}
          closable={true}
          open={showCal}
          onCancel={closeCalenderModal}
          footer={[]}
        >
          <CalenderModal />
        </Modal>
      )}

      <DeleteLead
        isMulti={!isDelete?.key}
        data={
          !isDelete?.key ? selectedRecords : { ...isDelete, id: isDelete?.key }
        }
        isOpen={isDelete}
        handleCancel={() => setDelete()}
      />
      <BulkUpdate isOpen={isEdit} handleCancel={() => setEdit(false)} />
      <ImportLeads open={isImport} handleCancel={() => setImport(false)} />
    </>
  );
};

export default LeadContent;
